import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { User } from '@shared/types'
import { 
  loginUser, 
  registerUser, 
  logoutUser, 
  refreshToken, 
  validateSession,
  getUserProfile 
} from '../thunks/authThunks'
import { tokenManager } from '@/services/api'

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  redirectUrl: string | null
}

// Initialize state from localStorage if available
const initializeState = (): AuthState => {
  const token = tokenManager.getToken()
  const isTokenValid = tokenManager.isTokenValid()

  return {
    user: null,
    token: isTokenValid ? token : null,
    isAuthenticated: isTokenValid,
    isLoading: false, // Don't auto-set loading - let components/hooks manage this
    error: null,
    redirectUrl: null,
  }
}

const initialState: AuthState = initializeState()

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Manual logout action (for client-side only logout)
    logout: (state) => {
      state.user = null
      state.token = null
      state.isAuthenticated = false
      state.error = null
      state.redirectUrl = null
      tokenManager.removeToken()
    },
    clearError: (state) => {
      state.error = null
    },
    setRedirectUrl: (state, action: PayloadAction<string>) => {
      state.redirectUrl = action.payload
    },
    clearRedirectUrl: (state) => {
      state.redirectUrl = null
    },
    // Action to set loading state manually if needed
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    // Action for Entra ID authentication success
    setEntraAuthSuccess: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.isLoading = false
      state.isAuthenticated = true
      state.user = action.payload.user
      state.token = action.payload.token
      state.error = null
      tokenManager.setToken(action.payload.token)
    },
  },
  extraReducers: (builder) => {
    // Login user cases
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false
        state.isAuthenticated = true
        state.user = action.payload.user
        state.token = action.payload.token
        state.error = null
        tokenManager.setToken(action.payload.token)
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false
        state.isAuthenticated = false
        state.user = null
        state.token = null
        state.error = action.payload || 'Login failed'
        tokenManager.removeToken()
      })

    // Register user cases
    builder
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false
        state.isAuthenticated = true
        state.user = action.payload.user
        state.token = action.payload.token
        state.error = null
        tokenManager.setToken(action.payload.token)
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false
        state.isAuthenticated = false
        state.user = null
        state.token = null
        state.error = action.payload || 'Registration failed'
        tokenManager.removeToken()
      })

    // Logout user cases
    builder
      .addCase(logoutUser.pending, (state) => {
        // Don't set loading for logout - it should be immediate
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null
        state.token = null
        state.isAuthenticated = false
        state.error = null
        state.redirectUrl = null
      })
      .addCase(logoutUser.rejected, (state) => {
        // Even if server logout fails, clear local state
        state.user = null
        state.token = null
        state.isAuthenticated = false
        state.error = null
        state.redirectUrl = null
      })

    // Refresh token cases
    builder
      .addCase(refreshToken.pending, (state) => {
        // Don't show loading for token refresh - should be background
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.user = action.payload.user
        state.token = action.payload.token
        state.isAuthenticated = true
        state.error = null
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.user = null
        state.token = null
        state.isAuthenticated = false
        state.error = action.payload || 'Token refresh failed'
        tokenManager.removeToken()
      })

    // Validate session cases
    builder
      .addCase(validateSession.pending, (state) => {
        state.isLoading = true
      })
      .addCase(validateSession.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload.user
        state.isAuthenticated = true
        state.error = null
      })
      .addCase(validateSession.rejected, (state) => {
        state.isLoading = false
        state.user = null
        state.token = null
        state.isAuthenticated = false
        state.error = null // Don't show error for session validation
        tokenManager.removeToken()
      })

    // Get user profile cases
    builder
      .addCase(getUserProfile.pending, (state) => {
        // Don't set loading for profile fetch if user already exists
        if (!state.user) {
          state.isLoading = true
        }
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload.user
        state.isAuthenticated = true
        state.error = null
      })
      .addCase(getUserProfile.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || 'Failed to get user profile'
        // Don't clear auth state for profile fetch failure
      })
  },
})

export const { 
  logout, 
  clearError,
  setRedirectUrl,
  clearRedirectUrl,
  setLoading,
  setEntraAuthSuccess,
} = authSlice.actions

export default authSlice.reducer 